import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, BarChart } from './_finHelpers';

// Finance Calculator — TVM with 5 tabs (FV, PMT, I/Y, N, PV)
export default function FinanceCalc() {
  const [solveFor, setSolveFor] = useState('FV');
  const [N, setN] = useState(10);
  const [IY, setIY] = useState(6);
  const [PV, setPV] = useState(20000);
  const [PMT, setPMT] = useState(-2000);
  const [FV, setFV] = useState(0);
  const [when, setWhen] = useState('end'); // 'begin' | 'end'
  const [cpy, setCpy] = useState(1); // compounds per year (P/Y = 1 default)
  const [ppy, setPpy] = useState(1); // payments per year

  const c = useMemo(() => {
    const iPer = Math.pow(1 + IY/100/cpy, cpy/ppy) - 1;
    const nPer = N * ppy;
    const b = when === 'begin' ? 1 : 0;

    const buildSchedule = (pvVal, pmtVal, iVal, nP, bTiming) => {
      const rows = [];
      let bal = -pvVal; // conventional sign: PV as inflow => negative balance? treat balance as absolute amount held
      let cumDep = 0, cumInt = 0;
      const cap = Math.min(nP, 120);
      for (let p = 1; p <= cap; p++) {
        if (bTiming === 1) { bal -= pmtVal; cumDep += -pmtVal; }
        const int = bal * iVal;
        cumInt += int; bal += int;
        if (bTiming === 0) { bal -= pmtVal; cumDep += -pmtVal; }
        rows.push({ period: p, perDeposit: -pmtVal, periodInt: int, end: bal, principal: -pvVal, deposits: cumDep, interest: cumInt });
      }
      return rows;
    };

    if (solveFor === 'FV') {
      let fv;
      if (iPer === 0) fv = -(PV + PMT * nPer);
      else fv = -(PV * Math.pow(1 + iPer, nPer) + PMT * (1 + iPer * b) * (Math.pow(1 + iPer, nPer) - 1) / iPer);
      return { FV: fv, sumPMT: PMT * nPer, totalInterest: fv + PV + PMT * nPer, schedule: buildSchedule(PV, PMT, iPer, nPer, b) };
    }
    if (solveFor === 'PMT') {
      let pmt;
      if (iPer === 0) pmt = -(PV + FV) / nPer;
      else pmt = -(PV * Math.pow(1 + iPer, nPer) + FV) / ((1 + iPer * b) * (Math.pow(1 + iPer, nPer) - 1) / iPer);
      return { PMT: pmt, sumPMT: pmt * nPer, totalInterest: FV + PV + pmt * nPer, schedule: buildSchedule(PV, pmt, iPer, nPer, b) };
    }
    if (solveFor === 'PV') {
      let pv;
      if (iPer === 0) pv = -FV - PMT * nPer;
      else pv = -(FV + PMT * (1 + iPer * b) * (Math.pow(1 + iPer, nPer) - 1) / iPer) / Math.pow(1 + iPer, nPer);
      return { PV: pv, sumPMT: PMT * nPer, totalInterest: FV + pv + PMT * nPer, schedule: buildSchedule(pv, PMT, iPer, nPer, b) };
    }
    if (solveFor === 'N') {
      let lo = 0.001, hi = 500;
      for (let i = 0; i < 60; i++) {
        const mid = (lo + hi) / 2;
        const val = PV * Math.pow(1 + iPer, mid * ppy) + PMT * (1 + iPer * b) * (Math.pow(1 + iPer, mid * ppy) - 1) / iPer + FV;
        if (val > 0) lo = mid; else hi = mid;
      }
      const solvedN = (lo + hi)/2;
      return { N: solvedN, sumPMT: PMT * (solvedN * ppy), totalInterest: FV + PV + PMT * (solvedN * ppy), schedule: buildSchedule(PV, PMT, iPer, solvedN * ppy, b) };
    }
    if (solveFor === 'IY') {
      let lo = -50, hi = 100;
      for (let i = 0; i < 80; i++) {
        const mid = (lo + hi) / 2;
        const iM = Math.pow(1 + mid/100/cpy, cpy/ppy) - 1;
        const val = PV * Math.pow(1 + iM, nPer) + PMT * (1 + iM * b) * (Math.pow(1 + iM, nPer) - 1) / (iM || 1e-9) + FV;
        if (val > 0) lo = mid; else hi = mid;
      }
      const solvedIY = (lo + hi)/2;
      const iM = Math.pow(1 + solvedIY/100/cpy, cpy/ppy) - 1;
      return { IY: solvedIY, sumPMT: PMT * nPer, totalInterest: FV + PV + PMT * nPer, schedule: buildSchedule(PV, PMT, iM, nPer, b) };
    }
    return {};
  }, [solveFor, N, IY, PV, PMT, FV, when, cpy, ppy]);

  const tabs = ['FV','PMT','I/Y','N','PV'];

  return (
    <CalcShell look="cnet" title="Finance Calculator" subtitle="Time-Value-of-Money calculator — solve for FV, PV, PMT, N or I/Y."
      about={<><p>This is a general-purpose financial calculator that solves the standard <b>time value of money</b> equation. Enter any four variables (N, I/Y, PV, PMT, FV) to solve for the fifth.</p></>}
    >
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        {tabs.map(t => (
          <button key={t} onClick={()=>setSolveFor(t.replace('/',''))} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${solveFor===t.replace('/','')?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`} data-testid={`fin-tab-${t.replace('/','').toLowerCase()}`}>{t}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          {solveFor !== 'N' && <Field label="N (# of periods)" value={N} onChange={setN}/>}
          {solveFor !== 'IY' && <Field label="I/Y (Interest per year)" value={IY} onChange={setIY} suffix="%"/>}
          {solveFor !== 'PV' && <Field label="PV (Present Value)" value={PV} onChange={setPV} prefix="$"/>}
          {solveFor !== 'PMT' && <Field label="PMT (Periodic Payment)" value={PMT} onChange={setPMT} prefix="$"/>}
          {solveFor !== 'FV' && <Field label="FV (Future Value)" value={FV} onChange={setFV} prefix="$"/>}
          <div className="field mt-3"><label>Settings</label>
            <div className="grid grid-cols-2 gap-2">
              <select value={cpy} onChange={e=>setCpy(+e.target.value)}>
                <option value={1}>C/Y = 1 (annual)</option><option value={2}>C/Y = 2</option><option value={4}>C/Y = 4</option><option value={12}>C/Y = 12 (monthly)</option>
              </select>
              <select value={ppy} onChange={e=>setPpy(+e.target.value)}>
                <option value={1}>P/Y = 1 (annual)</option><option value={12}>P/Y = 12 (monthly)</option>
              </select>
              <select value={when} onChange={e=>setWhen(e.target.value)}>
                <option value="end">END (ordinary)</option><option value="begin">BEGIN (annuity due)</option>
              </select>
            </div>
          </div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Solved: {solveFor}</div><div className="result-value" data-testid={`fin-result-${solveFor.toLowerCase()}`}>{
            solveFor === 'FV' ? fmt(c.FV) :
            solveFor === 'PMT' ? fmt(c.PMT) :
            solveFor === 'PV' ? fmt(c.PV) :
            solveFor === 'N' ? (c.N?.toFixed(3) + ' years') :
            solveFor === 'IY' ? (c.IY?.toFixed(3) + '%') : ''
          }</div></div>
          <table className="data-table"><tbody>
            <tr><td>Sum of all periodic payments</td><td className="text-right">{fmt(c.sumPMT)}</td></tr>
            <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.totalInterest)}</td></tr>
          </tbody></table>
        </div>
      </div>

      {c.schedule && c.schedule.length > 0 && (
        <>
          <div className="calc-card p-5 mt-6">
            <h3 className="font-bold text-slate-900 mb-3">Balance Accumulation Graph</h3>
            <BarChart
              data={c.schedule.map(r => ({
                label: String(r.period),
                values: [
                  { value: r.principal, color: '#2563eb' },
                  { value: r.deposits,  color: '#10b981' },
                  { value: r.interest,  color: '#f97316' },
                ]
              }))}
              stack={true}
              legend={[
                { label: 'Starting Amount', color: '#2563eb' },
                { label: 'Deposits',        color: '#10b981' },
                { label: 'Interest',        color: '#f97316' },
              ]}
            />
          </div>
          <div className="calc-card p-5 mt-6">
            <h3 className="font-bold text-slate-900 mb-3">Schedule</h3>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm border border-slate-200">
                <thead className="bg-blue-800 text-white sticky top-0"><tr><th className="px-2 py-1.5">Period</th><th className="px-2 py-1.5 text-right">Deposit</th><th className="px-2 py-1.5 text-right">Interest</th><th className="px-2 py-1.5 text-right">Ending Balance</th></tr></thead>
                <tbody>
                  {c.schedule.map((r,i) => (<tr key={i} className={i%2?'bg-slate-50':''}><td className="px-2 py-1.5">{r.period}</td><td className="px-2 py-1.5 text-right">{fmt(r.perDeposit)}</td><td className="px-2 py-1.5 text-right text-orange-600">{fmt(r.periodInt)}</td><td className="px-2 py-1.5 text-right font-semibold">{fmt(r.end)}</td></tr>))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </CalcShell>
  );
}

function Field({ label, value, onChange, prefix, suffix }) {
  return (
    <div className="field"><label>{label}</label>
      <div className="flex gap-2">
        {prefix && <span className="self-center text-slate-500">{prefix}</span>}
        <input type="number" step="any" value={value} onChange={e=>onChange(+e.target.value||0)} className="flex-1"/>
        {suffix && <span className="self-center text-slate-500">{suffix}</span>}
      </div>
    </div>
  );
}
